/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { Page, Text, View, Document, StyleSheet, Image, Font } from "@react-pdf/renderer";
import Constants from "utilities/Constants";
import { formatDeviceAddress } from "utilities/DeviceNameFormatter";
import Strings from "utilities/StringSelector";
import Utils from "utilities/Utils";

// Register Roboto Font
Font.register({ family: "RobotoRegular", src: `${process.env.PUBLIC_URL}/fonts/roboto-v20-latin-regular.ttf` });
Font.register({ family: "RobotoMedium", src: `${process.env.PUBLIC_URL}/fonts/Roboto-Medium.ttf` });

const UtilsInstance = new Utils();

export class MonitoringReport extends Component {
  constructor(props) {
    super(props);

    this.state = {
      generatingDate: new Date(),
      // Summary
      monitoringFirstResumePage: -1,
      monitoringFirstLiveListPage: -1,
      monitoringFirstLogPage: -1,
      // Summary numbering control
      isFirstResumePage: true,
      isFirstLiveListPage: true,
      isFirstLogPage: true,
    };
  }

  // ------------------------ Utilities -------------------------
  getDateNow = () => {
    return (
      this.state.generatingDate.getDate()
      + "/"
      + (this.state.generatingDate.getMonth() + 1)
      + "/"
      + this.state.generatingDate.getFullYear()
    );
  }

  getRectImage = (isFilled) => {
    if (isFilled) {
      return <Image src={`${process.env.PUBLIC_URL}/images/filled-rect.png`} style={generalInfoStyles.rectangle} />;
    }
    else {
      return <Image src={`${process.env.PUBLIC_URL}/images/empty-rect.png`} style={generalInfoStyles.rectangle} />;
    }
  }

  getDateTimeNow = () => {
    let year = this.state.generatingDate.getFullYear();
    let month = this.state.generatingDate.getMonth() + 1;
    let day = this.state.generatingDate.getDate();
    let hour = this.state.generatingDate.getHours();
    let minutes = this.state.generatingDate.getMinutes();
    let seconds = this.state.generatingDate.getSeconds();

    if (day < 10) { day = "0" + day; }
    if (month < 10) { month = "0" + month; }
    if (hour < 10) { hour = "0" + hour; }
    if (minutes < 10) { minutes = "0" + minutes; }
    if (seconds < 10) { seconds = "0" + seconds; }

    return (day + "/" + month + "/" + year + " " + hour + ":" + minutes + ":" + seconds);
  }

  getBaudRateText = (baudRate) => {
    let baudRateKbps = baudRate / 1000;
    let baudRateMbps = baudRate / 1000000;

    // If the baud rate is greater then 1000 Kbps show it as Mbps
    if (baudRateKbps >= 1000.00) {
      return baudRateMbps.toString().replace(".", ",") + " Mbps";
    }
    else {
      return baudRateKbps.toString().replace(".", ",") + " Kbps";
    }
  }

  // ----------------- Header for all the pages -----------------
  getPageHeader = () => {
    return (
      <View fixed>
        <View style={headerStyles.container}>
          <Image src={`${process.env.PUBLIC_URL}/logo_report.png`} style={headerStyles.logo} />
          <Text style={headerStyles.text}>{Strings.HEADER_TEXT_PROTOCOL}</Text>
        </View>
        <View style={headerStyles.line}></View>
      </View>
    );
  }

  // ----------------- Footer for all the pages -----------------
  getPageFooter = (reportPart) => {
    return (
      <View style={footerStyles.container} fixed>
        <View style={footerStyles.line}></View>
        <View>
          <Text style={footerStyles.text} render={({ pageNumber, totalPages }) => {
            this.setFirstPageNumbers(reportPart, pageNumber);
            return (`${this.getDateNow()}, Página ${pageNumber} / ${totalPages}`);
          }} fixed />
        </View>
      </View>
    );
  }

  setFirstPageNumbers = (reportPart, pageNumber) => {
    if (reportPart === "monitoringResume" && this.state.isFirstResumePage) {
      this.setState({
        monitoringFirstResumePage: pageNumber,
        isFirstResumePage: false
      });
    }
    else if (reportPart === "monitoringLiveList" && this.state.isFirstLiveListPage) {
      this.setState({
        monitoringFirstLiveListPage: pageNumber,
        isFirstLiveListPage: false
      });
    }
    else if (reportPart === "monitoringLog" && this.state.isFirstLogPage) {
      this.setState({
        monitoringFirstLogPage: pageNumber,
        isFirstLogPage: false
      });
    }
  }

  // ----------------- Generate Report --------------------------
  createReport = () => {
    return (
      <Document
        title={Strings.REPORT_EXPORT_NAME_PROTOCOL}>
        {this.createGeneralInfoPage()}
        {this.createDataInfoPage()}
        {this.createSummaryPage()}
        {this.creteResumePage()}
        {this.createMonitoringLiveListPages()}
        {this.createMonitoringLogPages()}
        {this.createDisclaimerPage()}
      </Document>
    );
  }

  // ----------------- General Information ---------------------
  createGeneralInfoPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <Text style={reusableStyles.pageTitle}>{Strings.GENERAL_REPORT}</Text>

        <Text style={reusableStyles.subHeaderText}>{Strings.CLIENT_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }} >

          <View style={{ maxWidth: "400pt" }}>
            <Text style={reusableStyles.normalText}>{Strings.PROJECT_NAME} {this.props.userInput.clientProjectName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.NETWORK_NAME} {this.props.userInput.clientNetworkName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.LOCATION} {this.props.userInput.clientLocation}</Text>
            <Text style={reusableStyles.normalText}>{Strings.COMPANY_NAME} {this.props.userInput.clientCompanyName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.ACCOUNTABLE_NAME} {this.props.userInput.clientAccountableName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.COMPANY_ADDRESS} {this.props.userInput.clientAddress}</Text>
            <Text style={reusableStyles.normalText}>{Strings.COMPANY_CITY} {this.props.userInput.clientCity}</Text>
            <Text style={reusableStyles.lastInfoText}>{Strings.COMPANY_TEL} {this.props.userInput.clientTel}</Text>
          </View>

          <View style={{ maxWidth: "100pt" }}>
            {this.getClientImage()}
          </View>
        </View>

        <Text style={reusableStyles.subHeaderText}>{Strings.EXECUTING_COMPANY_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }} >

          <View style={{ maxWidth: "400pt" }}>
            <Text style={reusableStyles.normalText}>{Strings.EXECUTING_COMPANY_NAME} {this.props.userInput.executingCompanyName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.EXECUTING_ACCOUNTABLE_NAME} {this.props.userInput.executingAccountableName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.EXECUTING_TEL} {this.props.userInput.executingTel}</Text>
            <Text style={reusableStyles.lastInfoText}>{Strings.EXECUTING_EMAIL} {this.props.userInput.executingEmail}</Text>
          </View>

          <View style={{ maxWidth: "100pt" }}>
            {this.getExecutorImage()}
          </View>
        </View>

        <Text style={reusableStyles.subHeaderText}>{Strings.ANNOTATIONS}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={generalInfoStyles.notesSection}>
          <Text style={reusableStyles.normalText}>{this.props.userInput.notes}</Text>
        </View>

        <Text style={reusableStyles.subHeaderText}>{Strings.CONCLUSION}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={generalInfoStyles.conclusionSection}>

          <View>

            <View style={generalInfoStyles.rectangleSection}>
              {this.getRectImage(this.props.userInput.conformity === Constants.CONFORM)}
              <Text style={reusableStyles.normalText}>{Strings.CONFORM_STRING}</Text>
            </View>

            <View style={generalInfoStyles.rectangleSection}>
              {this.getRectImage(this.props.userInput.conformity === Constants.CONFORM_W_RESTRICTION)}
              <Text style={reusableStyles.normalText}>{Strings.CONFORM_W_RESTRICTION_STRING}</Text>
            </View>

            <View style={generalInfoStyles.rectangleSection}>
              {this.getRectImage(this.props.userInput.conformity === Constants.NON_CONFORM)}
              <Text style={reusableStyles.normalText}>{Strings.NON_CONFORM_STRING}</Text>
            </View>

          </View>

          <View style={generalInfoStyles.signatureLine} />

        </View>

        {this.getPageFooter("")}

      </Page >
    );
  }

  getClientImage = () => {
    // Only try to draw the Image on the pdf if the user has
    // submited a client image
    if (this.props.userInput.clientImg !== "") {
      return (
        <Image style={{
          maxHeight: "100pt",
          width: "100pt"
        }} src={this.props.userInput.clientImg} />
      );
    }
  }

  getExecutorImage = () => {
    // Only try to draw the Image on the pdf if the user has
    // submited a executor image
    if (this.props.userInput.executorImg !== "") {
      return (
        <Image style={{
          maxHeight: "100pt",
          width: "100pt"
        }} src={this.props.userInput.executorImg} />
      );
    }
  }

  // ----------------- Data Information ------------------------
  createDataInfoPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <Text style={reusableStyles.pageTitle}>{Strings.DATA_REPORT}</Text>

        <Text style={reusableStyles.subHeaderText}>{Strings.MONITORING_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.TAG_NAME_DN}{this.props.tag} </Text>
        <Text style={reusableStyles.normalText}>{Strings.MONITOR_VERSION_DN}{Constants.TS_MONITOR_VERSION} </Text>
        <Text style={reusableStyles.normalText}>{Strings.MONITOR_INIT_DN}{UtilsInstance.getMonitorInitTime(this.props.uptime, UtilsInstance)} </Text>
        <Text style={reusableStyles.normalText}>{Strings.GENERATING_TIME_DN}{this.getDateTimeNow()} </Text>
        <Text style={reusableStyles.normalText}>{Strings.DURATION_SINCE_MON_BEGIN}{UtilsInstance.formatToRelativeTime2(this.props.uptime, UtilsInstance)} </Text>

        <Text style={reusableStyles.secondSubHeaderText}>{Strings.ETHERNET_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.IP_DN}{this.props.configData.addr} </Text>
        <Text style={reusableStyles.normalText}>{Strings.MASK_DN}{this.props.configData.netmask} </Text>
        <Text style={reusableStyles.normalText}>{Strings.GATEWAY_DN}{this.props.configData.gateway} </Text>

        <Text style={reusableStyles.secondSubHeaderText}>{Strings.WLAN_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.NETWORK_NAME_DN}{this.props.wifiConfigData.wifiName}</Text>
        <Text style={reusableStyles.normalText}>{Strings.IP_DN}{this.props.wifiConfigData.wifiAddr}</Text>
        <Text style={reusableStyles.normalText}>{Strings.MASK_DN}{this.props.wifiConfigData.wifiNetmask}</Text>
        <Text style={reusableStyles.normalText}>{Strings.GATEWAY_DN}{this.props.wifiConfigData.wifiGateway}</Text>

        {this.getPageFooter("")}

      </Page >
    );
  }

  // ---------------------- Summary ----------------------------
  createSummaryPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <Text style={reusableStyles.pageTitle}>{Strings.SUMMARY}</Text>

        <Text style={reusableStyles.summaryText} render={() => (
          `1. ${Strings.RESUME} ${Strings.RESUME_DOTS} ${this.state.monitoringFirstResumePage}`
        )} fixed />

        <Text style={reusableStyles.summaryText} render={() => (
          `2. ${Strings.LIVE_LIST} ${Strings.LIVE_LIST_DOTS_PROTOCOL} ${this.state.monitoringFirstLiveListPage}`
        )} fixed />

        <Text style={reusableStyles.summaryText} render={() => (
          `3. ${Strings.LOG} ${Strings.MON_LOG_DOTS} ${this.state.monitoringFirstLogPage}`
        )} fixed />

        <Text style={reusableStyles.summaryText} render={({ totalPages }) => (
          `4. ${Strings.DISCLAIMER} ${Strings.DISCLAIMER_DOTS_PROTOCOL} ${totalPages}`
        )} fixed />

        {this.getPageFooter("")}

      </Page>
    );
  }

  // ----------------- Resumo -----------------
  creteResumePage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <Text style={monitoringLiveListStyles.pageTitle} fixed>{Strings.PROTOCOL_RESUME_PAGE_TITLE}</Text>

        <Text style={reusableStyles.subHeaderText}>{Strings.DEVICENET_NETWORK_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.BAUD_RATE_PROTOCOL}{this.getBaudRateText(this.props.statistics.baudRate)}</Text>
        <Text style={reusableStyles.normalText}>{Strings.OCUP_BANDA_DN}{this.props.statistics.ocupBanda.toFixed(0)}</Text>
        <Text style={reusableStyles.normalText}>{Strings.CYCLE_TIME_PROTOCOL}{this.props.statistics.cycleTime.toFixed(2).replace(".", ",") + " ms"}</Text>

        <Text style={reusableStyles.secondSubHeaderText}>{Strings.DEVICE_DATA}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.MASTERS_DN}{this.props.statistics.masters}</Text>
        <Text style={reusableStyles.normalText}>{Strings.SLAVES_DN}{this.props.statistics.num_devices}</Text>
        <Text style={reusableStyles.normalText}>{Strings.MISSING_DN}{this.props.statistics.missings}</Text>
        <Text style={reusableStyles.normalText}>{Strings.RETRIES_DN}{this.props.statistics.retriesTotal}</Text>
        <Text style={reusableStyles.normalText}>{Strings.DIAGNOSTIC_DN}{this.props.statistics.num_diag}</Text>
        <Text style={reusableStyles.normalText}>{Strings.FAULT_DN}{this.props.statistics.falhasTotal}</Text>
        <Text style={reusableStyles.normalText}>{Strings.ERROR_FRAME}{this.props.statistics.contErrorFrame}</Text>

        {this.getPageFooter("monitoringResume")}

      </Page >
    );
  }

  // ----------------- Live List -----------------
  createMonitoringLiveListPages = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}
        {this.getMonitoringLiveListPageTitle()}

        <View style={monitoringLiveListStyles.table}>
          {this.createMonitoringLiveListTableHeader()}
          {this.createMonitoringLiveListTable()}
        </View>

        {this.getPageFooter("monitoringLiveList")}

      </Page>
    );
  }

  getMonitoringLiveListPageTitle = () => {
    return (
      <Text style={monitoringLiveListStyles.pageTitle} fixed>{Strings.PROTOCOL_LIVE_LIST_PAGE_TITLE}</Text>
    );
  }

  createMonitoringLiveListTableHeader = () => {
    return (
      <View style={monitoringLiveListStyles.tableRow} fixed>

        <View style={monitoringLiveListStyles.addressTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.ADDRESS}</Text>
        </View>

        <View style={monitoringLiveListStyles.tagTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.TAG}</Text>
        </View>

        <View style={monitoringLiveListStyles.diagnosticsTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.DIAGNOSTICS_STRING}</Text>
        </View>

        <View style={monitoringLiveListStyles.retriesTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.RETRIES_STRING}</Text>
        </View>

        <View style={monitoringLiveListStyles.faultsTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.FAULTS_STRING}</Text>
        </View>

        <View style={monitoringLiveListStyles.statusTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.STATUS}</Text>
        </View>

      </View>
    );
  }

  createMonitoringLiveListTable = () => {
    return (
      this.props.livelist.map(device => (
        <View style={monitoringLiveListStyles.tableRow} key={device.address} wrap={false}>

          <View style={monitoringLiveListStyles.addressTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{formatDeviceAddress(device.address, Strings)}</Text>
          </View>

          <View style={monitoringLiveListStyles.tagTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{device.tag}</Text>
          </View>

          <View style={monitoringLiveListStyles.diagnosticsTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getDiagnosticsText(device)}</Text>
          </View>

          <View style={monitoringLiveListStyles.retriesTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getRetriesText(device)}</Text>
          </View>

          <View style={monitoringLiveListStyles.faultsTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getFaultsText(device)}</Text>
          </View>

          {this.createMonitoringStatusCell(device)}

        </View>
      ))
    );
  }

  getDiagnosticsText = (device) => {
    if (device.status === "master") {
      return "-";
    }
    else {
      return device.contDiag;
    }
  }

  getRetriesText = (device) => {
    if (device.status === "master") {
      return "-";
    }
    else {
      return device.retries;
    }
  }

  getFaultsText = (device) => {
    if (device.status === "master") {
      return "-";
    }
    else {
      return device.fault;
    }
  }

  createMonitoringStatusCell = (device) => {
    if (device.status === "ok") {
      return (
        <View style={monitoringLiveListStyles.okTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.OK_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "diag") {
      return (
        <View style={monitoringLiveListStyles.diagnosticTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.DIAGNOSTIC_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "fault") {
      return (
        <View style={monitoringLiveListStyles.faultTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.FAULT_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "master") {
      if (device.mestre !== null) {
        return (
          <View style={monitoringLiveListStyles.masterTableStatusCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{Strings.MASTER_STRING}</Text>
          </View>
        );
      }
    }
    else if (device.status === "missing") {
      return (
        <View style={monitoringLiveListStyles.missingTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.MISSING_SINGULAR_STRING}</Text>
        </View>
      );
    }
    else {
      return (
        <View style={monitoringLiveListStyles.missingTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.UNKNOWN_NAME}</Text>
        </View>
      );
    }
  }

  // ----------------- Log -----------------
  createMonitoringLogPages = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}
        <Text style={monitoringLogStyles.pageTitle} fixed>{Strings.PROTOCOL_LOG_PAGE_TITLE}</Text>
        <Text style={monitoringLogStyles.pageLastEventsText} fixed>{Strings.LAST_200_EVENTS}</Text>

        <View style={monitoringLogStyles.table}>
          {this.createMonitoringLogTableHeader()}
          {this.createMonitoringLogTable()}
        </View>

        {this.getPageFooter("monitoringLog")}

      </Page>
    );
  }

  createMonitoringLogTableHeader = () => {
    return (
      <View style={monitoringLogStyles.tableRow} fixed>

        <View style={monitoringLogStyles.horaryTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.HORARY}</Text>
        </View>

        <View style={monitoringLogStyles.addressTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.ADDRESS}</Text>
        </View>

        <View style={monitoringLogStyles.tagTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.TAG}</Text>
        </View>

        <View style={monitoringLogStyles.descriptionTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.DESCRIPTION}</Text>
        </View>

      </View>
    );
  }

  createMonitoringLogTable = () => {
    let last200Events = [];

    if (this.props.log.length > Constants.MAX_NUM_OF_LOG_EVENTS) {
      for (let i = 0; i < Constants.MAX_NUM_OF_LOG_EVENTS; i++) {
        last200Events.push(this.props.log[i]);
      }
    }
    else {
      for (let i = 0; i < this.props.log.length; i++) {
        last200Events.push(this.props.log[i]);
      }
    }

    return (
      last200Events.map(logEntry => (
        <View style={monitoringLogStyles.tableRow} key={logEntry.event} wrap={false}>

          <View style={monitoringLogStyles.horaryTableCol}>
            <Text style={monitoringLogStyles.tableCell}>{this.getMonitoringLogTime(logEntry)}</Text>
          </View>

          <View style={monitoringLogStyles.addressTableCol}>
            {logEntry.address !== undefined && logEntry.address !== null ? (
              <Text style={monitoringLogStyles.tableCell}>{formatDeviceAddress(logEntry.address, Strings)}</Text>
            ):(
              <Text style={monitoringLogStyles.tableCell}>-</Text>
            )}
          </View>

          <View style={monitoringLogStyles.modelTableCol}>
            <Text style={monitoringLogStyles.tableCell}>{this.getLogTag(logEntry)}</Text>
          </View>

          {this.getMonitoringLogDescription(logEntry)}

        </View>
      ))
    );
  }

  formatTime = (eventTimeInSeconds) => {
    if (eventTimeInSeconds !== undefined && eventTimeInSeconds !== null) {
      const eventTimeInMillis = eventTimeInSeconds * 1000;
      const uptimeInMillis = this.props.uptime * 1000;
      const timeNow = new Date().getTime();
      const time = timeNow - uptimeInMillis + eventTimeInMillis;
      const dateToShow = new Date(time);
      return (dateToShow);
    }
    else {
      return Strings.BEFORE_MONITOR_INIT_STR;
    }
  }

  getMonitoringLogTime = (logEntry) => {
    if (logEntry.ntp !== null && logEntry.ntp === true) {
      let parsedMillis = Date.parse(logEntry.time);
      let dateToShow = new Date(parsedMillis);

      let year = dateToShow.getFullYear();
      let month = dateToShow.getMonth() + 1;
      let day = dateToShow.getDate();
      let hour = dateToShow.getHours();
      let minutes = dateToShow.getMinutes();

      if (day < 10) { day = "0" + day; }
      if (month < 10) { month = "0" + month; }
      if (hour < 10) { hour = "0" + hour; }
      if (minutes < 10) { minutes = "0" + minutes; }

      return day + "/" + month + "/" + year + " " + hour + ":" + minutes;
    }
    else {
      if (logEntry.time === null) {
        return Strings.BEFORE_MONITOR_INIT_STR;
      }
      else {
        let dateToShow = this.formatTime(logEntry.time);
        let year = dateToShow.getFullYear();
        let month = dateToShow.getMonth() + 1;
        let day = dateToShow.getDate();
        let hour = dateToShow.getHours();
        let minutes = dateToShow.getMinutes();

        if (day < 10) { day = "0" + day; }
        if (month < 10) { month = "0" + month; }
        if (hour < 10) { hour = "0" + hour; }
        if (minutes < 10) { minutes = "0" + minutes; }

        return day + "/" + month + "/" + year + " " + hour + ":" + minutes;
      }
    }

  }

  getLogTag = (logEntry) => {
    if (logEntry.tag === "") {
      return "-";
    }
    else if (logEntry.tag === Strings.NETWORK_EVENT) {
      return "";
    }
    else {
      return logEntry.tag;
    }
  }

  getMonitoringLogDescription = (logEntry) => {
    if (logEntry.tipo === Constants.DIAGNOSTIC_LOG_EVENT_TYPE) {
      return (
        <View style={monitoringLogStyles.descriptionTableCol}>
          {logEntry.active ? (
            <>
            <Text style={monitoringLogStyles.logDescriptionTableCellTitle}>{Strings.SLAVE_DIAGNOSTIC}</Text>
            {
              logEntry.description.map(description => (
                <Text key={description} style={monitoringLogStyles.logDescriptionTableCell}>{"• " + description}</Text>
              ))
            }
            </>
            ) : (
              <Text style={monitoringLogStyles.logDescriptionTableCell}>{Strings.EVENT_CHECKED_SUCCESS_PROTOCOL}</Text>
            )}
        </View>
      );
    }
    else if (logEntry.tipo === Constants.RETRIES_LOG_EVENT_TYPE) {
      return (
        <View style={monitoringLogStyles.descriptionTableCol}>
          <Text style={monitoringLogStyles.logDescriptionTableCell}>{logEntry.active ? Strings.PROTOCOL_RETRIES_LOG_DESCRIPTION : Strings.EVENT_CHECKED_SUCCESS_PROTOCOL}</Text>
        </View>
      );
    }
    else if (logEntry.tipo === Constants.FAULTS_LOG_EVENT_TYPE) {
      return (
        <View style={monitoringLogStyles.descriptionTableCol}>
          <Text style={monitoringLogStyles.logDescriptionTableCell}>{logEntry.active ? "" : Strings.EVENT_CHECKED_SUCCESS_PROTOCOL}</Text>
        </View>
      );
    }
    else if (logEntry.tipo === Constants.ERROR_FRAME_LOG_EVENT_TYPE) {
      return (
        <View style={monitoringLogStyles.descriptionTableCol}>
          <Text style={monitoringLogStyles.logDescriptionTableCell}>{logEntry.active ? Strings.PROTOCOL_ERROR_FRAME_LOG_DESCRIPTION : Strings.EVENT_CHECKED_SUCCESS_PROTOCOL}</Text>
        </View>
      );
    }
    else {
      if (logEntry.address === "128") {
        return (
          <View style={monitoringLogStyles.descriptionTableCol}>
            {
              logEntry.description.map(description => (
                <React.Fragment key={description}>
                  <Text style={monitoringLogStyles.logDescriptionTableCellTitle}>
                    {Strings.NETWORK_EVENT}:
                  </Text>
                  <Text style={monitoringLogStyles.logDescriptionTableCellTitle}>
                    {Strings.STATUS_CHANGE}
                    <Text style={monitoringLogStyles.logDescriptionTableCell}>{description}</Text>
                  </Text>
                </React.Fragment>
              ))
            }
          </View>
        );
      }
      else {
        return (
          <View style={monitoringLogStyles.descriptionTableCol}>
            {
              logEntry.description.map(description => (
                <Text key={description} style={monitoringLogStyles.logDescriptionTableCellTitle}>
                  {Strings.STATUS_CHANGE}
                  <Text style={monitoringLogStyles.logDescriptionTableCell}>{description}</Text>
                </Text>
              ))
            }
          </View>
        );
      }
    }
  }

  // ----------------- Disclaimer -----------------
  createDisclaimerPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <View style={{ marginTop: "30vh" }} />

        <Text style={reusableStyles.pageTitle}>{Strings.END_OF_REPORT}</Text>

        <View style={{ marginTop: "23vh" }} />

        <Text style={{
          fontSize: 14,
          fontFamily: "RobotoMedium",
          marginBottom: "5px"
        }}>
          {Strings.DISCLAIMER}
        </Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text
          style={{
            fontSize: 12,
            marginBottom: 2,
            fontFamily: "RobotoRegular",
            textAlign: "justify"
          }}>
          {Strings.DISCLAIMER_TEXT}
        </Text>

        {this.getPageFooter("")}

      </Page >
    );
  }

  render() {
    return (
      this.createReport()
    );
  }
}

const reusableStyles = StyleSheet.create({
  page: {
    paddingTop: 16,
    paddingHorizontal: 40,
    paddingBottom: 56
  },

  pageTitle: {
    textAlign: "center",
    fontSize: 18,
    marginTop: "2vh",
    marginBottom: "4vh",
    fontWeight: "bold",
    fontFamily: "RobotoMedium"
  },

  subHeaderText: {
    fontSize: 12,
    fontFamily: "RobotoMedium"
  },

  secondSubHeaderText: {
    marginTop: "3vh",
    fontSize: 12,
    fontFamily: "RobotoMedium"
  },

  subHeaderLine: {
    height: "1px",
    backgroundColor: "black",
    marginTop: "0.5vh",
    marginBottom: "2vh"
  },

  normalText: {
    fontSize: 12,
    marginBottom: 2,
    fontFamily: "RobotoRegular"
  },

  lastInfoText: {
    fontSize: 12,
    marginBottom: "2vh",
    fontFamily: "RobotoRegular"
  },

  summaryText: {
    fontSize: 12,
    marginBottom: "1vh",
    fontFamily: "RobotoRegular"
  }
});

const headerStyles = StyleSheet.create({
  container: {
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },

  logo: {
    width: "92pt",
    height: "30pt",
    marginBottom: "6pt"
  },

  text: {
    fontSize: 12,
    fontFamily: "RobotoMedium"
  },

  line: {
    height: "1px",
    backgroundColor: "black",
    marginTop: "1vh",
    marginBottom: "2vh"
  }
});

const footerStyles = StyleSheet.create({
  container: {
    textAlign: "right",
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40
  },

  line: {
    height: "1px",
    backgroundColor: "black",
    marginTop: "1vh",
    marginBottom: "1vh"
  },

  text: {
    fontSize: 12,
    fontFamily: "RobotoRegular"
  }
});

const generalInfoStyles = StyleSheet.create({
  notesSection: {
    maxWidth: "510pt",
    width: "90vw",
    height: "14vh"
  },

  conclusionSection: {
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: "16vh"
  },

  rectangleSection: {
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center"
  },

  rectangle: {
    width: 28,
    height: 28,
    margin: "1vh vw"
  },
  signatureLine: {
    height: "1px",
    width: "40vw",
    backgroundColor: "black",
  }
});

const monitoringLiveListStyles = StyleSheet.create({
  pageTitle: {
    fontSize: 18,
    marginTop: "2vh",
    marginBottom: "4vh",
    fontFamily: "RobotoMedium"
  },

  table: {
    display: "table",
    width: "auto",
    alignItems: "center",
    justifyContent: "center"
  },

  tableRow: {
    flexDirection: "row"
  },

  addressTableColHeader: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    backgroundColor: "#bdbdbd"
  },

  retriesTableColHeader: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  faultsTableColHeader: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  diagnosticsTableColHeader: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  tagTableColHeader: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  statusTableColHeader: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  tableCellHeader: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "RobotoMedium"
  },

  tableCell: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoRegular"
  },

  blacklistTableCell: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    color: "white",
    fontFamily: "RobotoRegular"
  },

  addressTableCol: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderTopWidth: 0
  },

  retriesTableCol: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  faultsTableCol: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  diagnosticsTableCol: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  tagTableCol: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  okTableStatusCol: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "lime"
  },

  diagnosticTableStatusCol: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "yellow"
  },

  faultTableStatusCol: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "red"
  },

  masterTableStatusCol: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "white"
  },

  missingTableStatusCol: {
    width: "20%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#708090"
  },
});

const monitoringLogStyles = StyleSheet.create({
  pageTitle: {
    fontSize: 18,
    marginTop: "2vh",
    fontFamily: "RobotoMedium"
  },

  pageLastEventsText: {
    fontSize: 12,
    marginBottom: "3vh",
    fontFamily: "RobotoRegular"
  },

  table: {
    display: "table",
    width: "auto"
  },

  tableRow: {
    flexDirection: "row"
  },

  horaryTableColHeader: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    backgroundColor: "#bdbdbd"
  },

  tagTableColHeader: {
    width: "25%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  addressTableColHeader: {
    width: "10%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  descriptionTableColHeader: {
    width: "50%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  tableCellHeader: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "RobotoMedium"
  },

  horaryTableCol: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderTopWidth: 0
  },

  addressTableCol: {
    width: "10%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  modelTableCol: {
    width: "25%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  descriptionTableCol: {
    width: "50%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  tableCell: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoRegular"
  },

  logDescriptionTableCell: {
    textAlign: "left",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoRegular"
  },

  logDescriptionTableCellTitle: {
    textAlign: "left",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoMedium"
  }

});

export default MonitoringReport;
